
import { Component, Prop, Vue } from "vue-property-decorator";
import { IFabric, searchFabrics } from "@/models/fabric";
import { swatchUrl, swatchImageUrl } from "@/services/api";

const MAX_RESULTS = 8;

interface SelectedEntry {
  id: string;
  /** null when the id doesn't resolve against the catalog — see the template. */
  fabric: IFabric | null;
}

/**
 * Builds one of a fabric's comparison arrays (comparisons / binding_comparisons /
 * fringe_comparisons).
 *
 * The value is an array of ObjectId STRINGS, which is exactly what the API stores
 * and returns — it does not populate these arrays (the $lookup stages in
 * fabric_service.queryFabrics are commented out on purpose), so the ids are
 * resolved here against the catalog the parent already has in memory.
 *
 * Deliberately NOT coupled to `uses`. A fabric can be given fringe comparisons
 * while its fringe flag is off, and can be flagged for fringe with no comparisons
 * — the flag is an approval, the array is reference data, and staff build the two
 * up at different times. Gating one on the other would make preparation work
 * impossible, which is the opposite of what this tool is for.
 */
@Component
export default class FabricComparisonPicker extends Vue {
  /** ObjectId strings. v-model-compatible. */
  @Prop({ default: () => [] }) readonly value!: string[];
  @Prop({ default: "" }) readonly label!: string;
  @Prop({ default: "" }) readonly description!: string;
  /** The catalog to resolve ids and search against — passed down, never refetched. */
  @Prop({ default: () => [] }) readonly fabrics!: IFabric[];
  /** The fabric being edited. Excluded from results: nothing compares to itself. */
  @Prop({ default: "" }) readonly excludeId!: string;

  protected term = "";
  protected open = false;
  protected active = 0;

  $refs!: { input: HTMLInputElement };

  created(): void {
    document.addEventListener("mousedown", this.onDocumentMouseDown);
  }

  beforeDestroy(): void {
    document.removeEventListener("mousedown", this.onDocumentMouseDown);
  }

  private get byId(): Record<string, IFabric> {
    const map: Record<string, IFabric> = {};
    for (const f of this.fabrics) {
      if (f._id) map[f._id] = f;
    }
    return map;
  }

  /** Selected ids paired with their fabric, in the order they were added. */
  protected get selected(): SelectedEntry[] {
    return (this.value || []).map((id) => ({ id, fabric: this.byId[id] || null }));
  }

  protected get results(): IFabric[] {
    const taken = new Set(this.value || []);
    return searchFabrics(this.fabrics, this.term)
      .filter((f) => f._id && f._id !== this.excludeId && !taken.has(f._id))
      .slice(0, MAX_RESULTS);
  }

  protected move(delta: number): void {
    if (!this.results.length) return;
    this.active = Math.min(Math.max(this.active + delta, 0), this.results.length - 1);
  }

  protected add(fabric?: IFabric): void {
    if (!fabric?._id) return;
    if ((this.value || []).includes(fabric._id)) return;
    // Emit a NEW array rather than pushing into the prop: the parent owns this
    // value, and mutating a prop in place is both a Vue anti-pattern and a way to
    // slip past the parent's dirty-check, which compares serialized snapshots.
    this.$emit("input", [...(this.value || []), fabric._id]);
    this.term = "";
    this.active = 0;
    this.open = false;
  }

  protected remove(id: string): void {
    this.$emit("input", (this.value || []).filter((v) => v !== id));
  }

  protected close(): void {
    this.open = false;
  }

  protected thumb(fabric: IFabric | null): string {
    if (!fabric) return "";
    const bust = fabric.assets_updated_at;
    const base = fabric.image
      ? swatchImageUrl(fabric.image, bust)
      : swatchUrl(fabric.texture_key || fabric.mfr_code, bust);
    if (!base) return "";
    return `${base}${base.includes("?") ? "&" : "?"}width=32`;
  }

  private onDocumentMouseDown(e: MouseEvent): void {
    if (!this.open) return;
    if (!this.$el.contains(e.target as Node)) this.open = false;
  }
}
